/**
 * InternalDocs.tsx — lista dokumentów wewnętrznych tenanta (klasa 'internal').
 *
 * Moduł: crm_core (dokumenty w pakiecie bazowym, ADR-0083; dawniej osobny moduł doc_internal).
 * Backend: GET /api/internal-docs (Model B — widoczność per-firma).
 * Klasa 'internal' NIE ma stron (brak counterparty cache) i brak satelity.
 *
 * Kolumny RDZENNE data-driven z katalogu (`GET /system-modules/internal/fields`) przez
 * `buildCoreColumns` — klucz w wierszu odpowiedzi wyliczany mechanicznie ze sluga (camelCase);
 * `BARE_DOCUMENT_CORE_COLUMN_WHITELIST` (`internal-docs/route.ts`) jest 1:1 mechaniczny, patrz
 * kolokowany `columns-projection.integration.test.ts`. Numer systemowy (mono) i tytuł
 * (pogrubiony, nieukrywalny) mają bogaty render przez rejestr nadpisań
 * (`STANDARD_DOC_OVERRIDES` w `lib/documents/standard-document-columns` — wspólna konfiguracja
 * kolumn z listami dokumentów handlowych i specjalnych oraz z sekcją kokpitu decyzji; kolumny
 * procesu buduje wspólne `lib/documents/process-columns`); firma/opiekun/źródło idą dedykowanymi
 * budowniczymi (`buildCompanyColumn` / `buildDocumentCoreRefColumns`), więc są WYKLUCZONE z
 * `buildCoreColumns` (`excludeSlugs`); kategoria to też kolumna ręczna (`buildCategoryColumn` —
 * w katalogu `category_id` ma `showInList: false`). Kolumny procesu (aktywny proces / bieżący
 * etap) to pola WIRTUALNE silnika (poza katalogiem) — domyślnie widoczne gdy moduł procesu aktywny. Pola dodatkowe (ext) przez
 * `buildExtColumns`. Pilność i data dokumentu (`urgency`/`document_date`) są kolumnami NOWYMI —
 * katalog je oznacza `showInList=true`, ale strona (pre-migration) ich nie renderowała; dryf
 * domknięty przez migrację na silnik katalogowy (mirror `Contracts.tsx`).
 *
 * Uwaga: slug systemu = 'internal', route = '/internal-docs', api = '/internal-docs'.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_TYPE_FILTER_FIELD } from '@saas/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RotateCcw, Shield, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListViewModeToggle,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { listLoadRetry } from '@/components/configurable-table/load-error';
import {
  type ListResponseMeta,
  useServerListControls,
} from '@/components/configurable-table/useServerListControls';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DocumentTypePickerModal } from '@/components/documents/DocumentTypePickerModal';
import { IconButton } from '@/components/IconButton';
import { ModuleGuard } from '@/components/ModuleGuard';
import { MultiSelect } from '@/components/MultiSelect';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useActiveMenuItem } from '@/hooks/useActiveMenuItem';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { buildDocumentCoreRefColumns } from '@/lib/documents/document-core-ref-columns';
import {
  buildDocumentListFilterParams,
  useDocumentListExport,
} from '@/lib/documents/document-list-export';
import { buildProcessColumns } from '@/lib/documents/process-columns';
import {
  INTERNAL_PROCESS_LABEL_PREFIX,
  type InternalDocRow,
  STANDARD_DOC_ALWAYS_SLUGS,
  STANDARD_DOC_COLUMN_ID_TO_SLUG,
  STANDARD_DOC_EXCLUDE_SLUGS,
  STANDARD_DOC_OVERRIDES,
} from '@/lib/documents/standard-document-columns';
import { useDocumentTypeNarrowing } from '@/lib/documents/use-document-type-narrowing';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { listFilterSkipSlugs, useListFilterOptions } from '@/lib/list-filter-options';
import { baseFiltersToHeaderFilters } from '@/lib/menu/menu-item-view-runtime';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import { useEmployeesList } from '@/lib/use-tenant-people';

const INTERNAL_DOCS_KEY = 'internal-docs';

// Pola rysowane nadpisaniem kolumny albo osobnym builderem nie pobierają słownika opcji filtra.
const FILTER_OPTIONS_SKIP_SLUGS = listFilterSkipSlugs(
  STANDARD_DOC_OVERRIDES,
  STANDARD_DOC_EXCLUDE_SLUGS,
);

interface ListResponse {
  internalDocs: InternalDocRow[];
  limit: number;
  offset: number;
  total: number;
  /** Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`. */
  meta?: ListResponseMeta;
  /** Etykiety pól `reference` (sibling do wierszy) — patrz `ExtRefLabels`. */
  extRefLabels?: ExtRefLabels;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function InternalDocs() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showTypePicker, setShowTypePicker] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [showDeleted, setShowDeleted] = useState(false);

  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  // Tryb widoku + tryby dostępne w bieżącym layoucie (raport tabeli) — wspólny hook.
  const viewModes = useListViewMode();
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Sort serwerowy + paginacja (silnik list) — wspólny hook, patrz useServerListControls.ts.
  // `sortableSlugs` — `meta.sortable` z ostatniej odpowiedzi listy (`listQ` niżej), zasilane
  // PO zapytaniu jako REALNY stan (nie ref, jak `getRefLabels`) — kolumny core muszą się
  // przeliczyć, gdy metadane dotrą (patrz `columns` niżej). Brak `columnIdToSortSlug` — id
  // kolumny core = slug (wyliczany mechanicznie przez `buildCoreColumns`), fallback do
  // `columnId` w `useServerListControls` wystarcza (żadna kolumna tej listy nie sortuje po
  // innym polu niż jej własny slug — mirror pilota bez `PI_SORT_SLUG_OVERRIDES`, tu zbędne).
  const controls = useServerListControls();
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Zmiana filtrów w nagłówku wraca na pierwszą stronę — DWA callsite'y (ListFilterBar +
  // ConfigurableTable), więc zawinięte w jeden stabilny handler.
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  const [confirmDelete, setConfirmDelete] = useState<InternalDocRow | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<InternalDocRow | null>(null);

  // Zaznaczenie wierszy (checkbox po lewej) → akcje na zaznaczonych z menu „Akcje" (PHASE 25).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Zbiór kolumn FAKTYCZNIE widocznych w tabeli teraz — zasilany przez
  // `onVisibleColumnsChange` (ConfigurableTable), odpala się przy pierwszym rozwiązaniu
  // widoczności i przy każdej zmianie (toggle w gear, zapis Kreatora Listy). `null` przed
  // pierwszym wywołaniem -> `visibleColumnParams` niżej spada na `listLayout`/`defaultVisible`.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);

  // Aktywna firma (CompanySwitcher) — listing scope'owany po stronie backendu (Model B).
  // Tryb "Wszystkie firmy" → brak ?companyId= (backend zwraca unię po firmach usera).
  const activeCompanyScope = useActiveCompanyScope();
  const companyId = companyIdParam(activeCompanyScope);
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);
  // Moduł procesu aktywny → kolumny przekrojowe (aktywny proces / bieżący etap) domyślnie
  // widoczne (gdy brak układu). Nieaktywny → domyślnie ukryte (backend i tak nie dokłada dla
  // nich danych).
  const isProcessModuleActive = useMemo(
    () => me?.modules?.some((m) => m.code === 'processes') ?? false,
    [me?.modules],
  );

  // Pozycja menu (`?mi=<id>`) przez którą klient wszedł — narzuca filtry stałe skonfigurowane
  // w Kreatorze (np. moduł HR „Teczka" zawężony do klasy/typu dokumentu). Mirror
  // ModuleRecordsPage/RecordFormPage (`@/lib/menu/menu-item-view-runtime`).
  const activeMenuItem = useActiveMenuItem();
  const menuBaseFilters = activeMenuItem?.view?.baseFilters;

  // Dokleja `?mi=<id>` do nawigacji w głąb (nowy/detal), żeby formularz dokumentu
  // wewnętrznego znał kontekst pozycji menu — URL detalu jest osobną nawigacją,
  // nie dziedziczy query stringa listy.
  const withMenuItem = useWithMenuItem();

  // Zawężenie do wybranych systemowych typów dokumentu (np. DOKPR dla Teczki) — unia trzech
  // źródeł (URL `?documentTypeIds=`/legacy `?documentTypeId=` + filtr stały pozycji menu),
  // patrz `useDocumentTypeNarrowing`.
  const narrowedTypeIds = useDocumentTypeNarrowing();

  // Zmiana zakresu firmy, zawężenia do typu dokumentu (Teczka) lub filtrów stałych pozycji menu
  // („Warunki" — przejście do innej pozycji menu) zawęża wynik — reset strony jak przy
  // filtrach/sorcie (inaczej user może utknąć na pustej stronie N). Zaznaczenie czyścimy, bo
  // zawężony wynik może już nie zawierać zaznaczonych wierszy.
  useEffect(() => {
    controls.resetPage();
    setSelectedIds([]);
  }, [companyId, narrowedTypeIds, menuBaseFilters, controls.resetPage]);

  // Filtr „Pracownik" — kontrolka dedykowana (pole ext `employee` jest typu `reference`,
  // który świadomie nie ma popupu w nagłówku kolumny — patrz `mapExtFieldTypeToFilterCategory`
  // w `@/lib/ext-list-columns`). Lista pracowników zawężona do aktywnej firmy jak reszta
  // strony; „Wszystkie firmy" -> pełna lista widoczna userowi. Renderowana WYŁĄCZNIE
  // w kontekście Teczki (gating na `narrowedTypeIds` niżej w JSX) — ogólna lista
  // dokumentów wewnętrznych nie ma pola `employee`.
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const employeesQ = useEmployeesList({
    companyId: companyId ?? undefined,
    isActive: true,
    limit: 100,
  });
  const employeeOptions = useMemo(
    () =>
      (employeesQ.data?.employees ?? []).map((e) => ({
        value: e.id,
        label: [e.firstName, e.lastName].filter(Boolean).join(' '),
      })),
    [employeesQ.data?.employees],
  );

  // Katalog pól modułu `internal` (core + ext) — źródło kolumn dodatkowych (`extColumns`).
  const fieldsQ = useSystemModuleFields('internal');
  // Listy wyboru w wierszu filtrów, które trzeba wczytać: pozycje słowników pól `select` (dokumenty
  // wewnętrzne dziś nie mają słownikowych — status/pilność to enumy z kodu) i użytkownicy dla „Opiekuna".
  const filterOptions = useListFilterOptions(fieldsQ.data, FILTER_OPTIONS_SKIP_SLUGS);
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany
  // niżej PO `listQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();
  // Buildery kolumn biorą getter etykiet odwołań jako parametr — tabela dostaje getter z odpowiedzi
  // listy, eksport CSV (E16) getter z odpowiedzi eksportu (etykiety WSZYSTKICH wierszy pliku).
  const extColumnsFor = useCallback(
    (getLabels: () => ExtRefLabels | undefined) =>
      buildExtColumns<InternalDocRow>(fieldsQ.data ?? [], t, getLabels),
    [fieldsQ.data, t],
  );
  const extColumns = useMemo(() => extColumnsFor(getRefLabels), [extColumnsFor, getRefLabels]);

  const forcedListLayoutId = useForcedListLayoutId('internal');
  const tableId = forcedListLayoutId ? `internal::layout-${forcedListLayoutId}` : 'internal';

  // Layout zapisany przez usera (Kreator Listy) — źródło aktualnej widoczności kolumn dla
  // `visibleColumnParams` niżej (ConfigurableTable czyta ten sam cache po `tableId`).
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; numer systemowy/tytuł przez
  // rejestr nadpisań). Firma/opiekun/kategoria/źródło wykluczone (dedykowane buildery niżej).
  const coreColumnsFor = useCallback(
    (getLabels: () => ExtRefLabels | undefined) =>
      buildCoreColumns<InternalDocRow>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels: getLabels,
        overrides: STANDARD_DOC_OVERRIDES,
        excludeSlugs: STANDARD_DOC_EXCLUDE_SLUGS,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
        selectOptions: filterOptions.selectOptions,
      }),
    [
      fieldsQ.data,
      t,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.selectOptions,
    ],
  );
  const coreColumns = useMemo(() => coreColumnsFor(getRefLabels), [coreColumnsFor, getRefLabels]);

  // Kolumny przekrojowe silnika (poza katalogiem) — aktywny proces / bieżący etap. Domyślnie
  // widoczne gdy moduł procesu aktywny (inaczej byłyby kolumnami samych myślników). W trybie
  // PROJEKCJI (`?columns=` obecny, patrz `typed-executor.ts`) backend dokłada `activeProcessName`/
  // `currentStage` do wiersza WYŁĄCZNIE gdy slug jest w `?columns=` — patrz `coreIdToSlug` niżej.
  const processColumns = useMemo<ColumnDefinition<InternalDocRow>[]>(
    () =>
      buildProcessColumns<InternalDocRow>({
        t,
        labelKeyPrefix: INTERNAL_PROCESS_LABEL_PREFIX,
        isSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        isProcessModuleActive,
      }),
    [t, controls.isCoreColumnSortable, sortableSlugs, isProcessModuleActive],
  );

  // Skład kolumn listy — JEDNO miejsce dla tabeli i pliku CSV (eksport podaje własny getter etykiet
  // i kolumny katalogu/dodatkowe zbudowane z tym getterem).
  const columnsFor = useCallback(
    (
      getLabels: () => ExtRefLabels | undefined,
      core: ColumnDefinition<InternalDocRow>[],
      ext: ColumnDefinition<InternalDocRow>[],
    ): ColumnDefinition<InternalDocRow>[] => [
      ...core,
      buildCompanyColumn<InternalDocRow>({ companies, getRefLabels: getLabels, t }),
      // Kategoria — wspólny builder (`@/lib/ext-list-columns`), JEDNO źródło prawdy dla
      // Kontrahentów (pilot) + 7 klas dokumentów.
      buildCategoryColumn<InternalDocRow>({
        t,
        resolveSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
      }),
      // Opiekun / źródło — wyekstrahowane do wspólnego buildera (6 list dokumentów kopiowały
      // ten sam blok 1:1) — `@/lib/documents/document-core-ref-columns`. `withSource: true` —
      // umowy/pisma/handlowe/wewnętrzne (STANDARD) MAJĄ kolumnę źródła na liście (faktury
      // renderują ją osobno przez `SourceBadge`, inny słownik).
      ...buildDocumentCoreRefColumns<InternalDocRow>({
        t,
        getRefLabels: getLabels,
        withSource: true,
        resolveSourceSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        resolveSourceFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
        // Filtr opiekuna: lista użytkowników (`record-select`), gdy serwer go serwuje i lista się wczytała.
        resolveOwnerFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
        ownerFilterOptions: filterOptions.ownerOptions,
      }),
      ...processColumns,
      ...ext,
    ],
    [
      companies,
      t,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.ownerOptions,
      processColumns,
    ],
  );
  const columns = useMemo(
    () => columnsFor(getRefLabels, coreColumns, extColumns),
    [columnsFor, getRefLabels, coreColumns, extColumns],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug dla kolumn wyprodukowanych przez
  // `buildCoreColumns` (pętla niżej); firma/opiekun/kategoria/źródło/proces mają dedykowane id
  // (firma: `company`, reszta: id już = slug, ale poza pętlą — dedykowane buildery, nie
  // `coreColumns`) więc wpisane jawnie.
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = { ...STANDARD_DOC_COLUMN_ID_TO_SLUG };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);
  const extSlugSet = useMemo(() => new Set(extColumns.map((c) => c.id)), [extColumns]);

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku (mode=values/condition/range) +
  // (2) input „Szukaj…" pod headerem (konwersja na mode=condition contains).
  const viewHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    // Filtr „Pracownik" (kontrolka dedykowana, patrz `employeeIds` wyżej) — pole ext
    // `employee`, wielowartościowy `mode: 'values'`.
    if (employeeIds.length > 0) {
      merged.push({ field: 'employee', mode: 'values', selectedValues: employeeIds });
    }

    // Filtry stałe z pozycji menu (`?mi=`) — dokładane WYŁĄCZNIE tutaj (poza reactowym
    // stanem headerFilters), więc są niewidoczne i nieusuwalne dla usera; łączone AND
    // z jego własnymi filtrami (mirror ModuleRecordsPage). Filtr typu dokumentu
    // (`DOCUMENT_TYPE_FILTER_FIELD`) jest WYCIĘTY stąd — silnik list go nie wystawia jako
    // ogólną kolumnę (patrz `extractDocumentTypeIdsFromBaseFilters`), podróżuje wyłącznie
    // przez `?documentTypeIds=` (`narrowedTypeIds` niżej), nie przez `headerFilters`.
    merged.push(
      ...baseFiltersToHeaderFilters(
        (menuBaseFilters ?? []).filter((f) => f.field !== DOCUMENT_TYPE_FILTER_FIELD),
        me?.user.id,
      ),
    );

    return merged;
  }, [headerFilters, debouncedColumnFilters, columns, employeeIds, menuBaseFilters, me?.user.id]);
  // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
  // tylko zapytanie listy. Eksport CSV (E16) bierze same filtry widoku: miesiąc to nawigacja.
  const exportHeaderFilters =
    viewHeaderFilters.length > 0 ? JSON.stringify(viewHeaderFilters) : undefined;
  const serializedHeaderFilters = controls.calendarHeaderFilter
    ? JSON.stringify([...viewHeaderFilters, controls.calendarHeaderFilter])
    : exportHeaderFilters;

  // Kolumny formatowania warunkowego (Kreator Listy) mogą celować w kolumnę aktualnie
  // NIEwidoczną (schowaną, ale z aktywną regułą) — `extraColumnIds` dopisuje jej slug do
  // `?columns=`, żeby reguła miała dane do ewaluacji nawet gdy kolumna sama nie jest wyświetlana.
  const formattingRuleColumnIds = useMemo(
    () => listLayout?.layoutConfig?.formattingRules?.map((r) => r.columnId) ?? [],
    [listLayout?.layoutConfig?.formattingRules],
  );

  // Kolumny aktualnie widoczne, jako slugi backendu (wspólny helper) — do zbudowania
  // `?columns=`, żeby ODPOWIEDŹ (JSON) i odczyt pól dodatkowych (ext) ograniczały się do
  // faktycznie renderowanych kolumn — SQL SELECT rdzenia po stronie backendu zostaje pełny
  // niezależnie od tego (patrz komentarz w `apps/api/app/api/internal-docs/route.ts`).
  // `gcrecord` w `alwaysSlugs` — czyta go akcja wiersza (restore/delete), nie jest kolumną.
  // Źródło widoczności: `visibleColumnIds` (efektywna widoczność z ConfigurableTable, patrz
  // `onVisibleColumnsChange` niżej) gdy już rozwiązana; przed pierwszym wywołaniem — fallback
  // na zapisany layout (`listLayout`) / `defaultVisible` (jak dotychczas).
  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns:
          visibleColumnIds !== null
            ? columns.map((c) => ({ id: c.id, visible: visibleColumnIds.includes(c.id) }))
            : listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet,
        alwaysSlugs: STANDARD_DOC_ALWAYS_SLUGS,
        extraColumnIds: formattingRuleColumnIds,
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      visibleColumnIds,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extSlugSet,
      formattingRuleColumnIds,
    ],
  );

  // Parametry filtrów listy BEZ stronicowania i projekcji kolumn — wspólne dla zapytania listy
  // i eksportu CSV (E16: plik zawiera dokładnie to, co lista).
  function buildListFilterParams({
    includeCalendar,
  }: {
    includeCalendar: boolean;
  }): URLSearchParams {
    const listHeaderFilters = includeCalendar ? serializedHeaderFilters : exportHeaderFilters;
    return buildDocumentListFilterParams({
      search: debouncedSearch,
      showArchived: showDeleted,
      companyId,
      // Teczka — zawężenie do zbioru typów (ten sam parametr co lista; serwer rozwija kopie typów).
      extra: {
        documentTypeIds: narrowedTypeIds.length > 0 ? narrowedTypeIds.join(',') : undefined,
      },
      headerFilters: listHeaderFilters,
      sort: controls.queryParams.sort,
      order: controls.queryParams.order,
    });
  }

  const queryKey = [
    INTERNAL_DOCS_KEY,
    {
      q: debouncedSearch,
      include_deleted: showDeleted,
      companyId: companyId ?? 'all',
      documentTypeIds: narrowedTypeIds.length > 0 ? narrowedTypeIds : undefined,
      headerFilters: serializedHeaderFilters,
      columns: visibleColumnParams,
      ...controls.queryParams,
    },
  ];

  const listQ = useQuery({
    queryKey,
    // Trzyma poprzednie dane na ekranie podczas refetchu wywołanego zmianą klucza (kolumny
    // widoczne się jeszcze „ustalają" — fields/layout/ConfigurableTable rozwiązują się
    // asynchronicznie, każde domyka `visibleColumnParams` inaczej) — bez tego lista miga
    // loaderem 2-3 razy przy każdym wejściu.
    placeholderData: keepPreviousData,
    // Awaria → jedno automatyczne ponowienie; zły adres / brak uprawnień / nieaktywny moduł →
    // bez ponawiania (komunikat od razu).
    retry: listLoadRetry,
    queryFn: async () => {
      const params = buildListFilterParams({ includeCalendar: true });
      if (visibleColumnParams.length > 0) {
        params.set('columns', visibleColumnParams.join(','));
      }
      // Paginacja serwerowa (silnik list) — `limit`/`offset` zawsze wysyłane.
      params.set('limit', String(controls.queryParams.limit));
      params.set('offset', String(controls.queryParams.offset));
      const res = await api.get<ListResponse>(`/internal-docs?${params.toString()}`);
      return res.data;
    },
  });
  setRefLabels(listQ.data?.extRefLabels);
  // `meta.sortable` w stan przez EFEKT, nie w renderze (to useState → wywołanie w renderze pętli).
  useEffect(() => {
    setSortableSlugs(listQ.data?.meta?.sortable);
  }, [listQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(listQ.data?.meta?.filterable);
  }, [listQ.data?.meta?.filterable]);

  // Usuwanie i przywracanie z listy to zwykły zapis dokumentu — na czas zadania przycisku SQL na nim serwer
  // odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje dokument.
  const reportRecordLock = useReportRecordLock();

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/internal-docs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INTERNAL_DOCS_KEY] });
      toast.show({ variant: 'success', title: t('tenant.documents.internal.toast.deleted') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.documents.internal.toast.deleteError') });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/internal-docs/${id}/restore`, {});
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: [INTERNAL_DOCS_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['internal-doc', id] });
      toast.show({ variant: 'success', title: t('tenant.documents.internal.toast.restored') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.documents.internal.toast.restoreError') });
    },
  });

  // Akcje listy (PHASE 25) — generyczny hook: resolver widoczności (katalog ∩ kartoteka ∩
  // moduły ∩ uprawnienia) + wykonawca (rozgłoszenie po ID na istniejące endpointy) + menu
  // kontekstowe (prawy klik) + potwierdzenia. Kartoteka `documents`, klasa `internal` → tylko
  // akcje uniwersalne (KSeF/FK dotyczą wyłącznie faktur). Import bez handlera → „wkrótce".
  // BEZ bramki „Wszystkie firmy": archiwizacja/przywróć biorą firmę z samego dokumentu (nie z
  // nagłówka X-Company-Id), więc działają też w trybie unii firm.
  //
  // Eksport CSV (E16) — dokładnie to, co lista: „Eksport listy" = te same filtry (wyszukiwarka,
  // archiwum, firma, filtry kolumn, sort, typ dokumentu z pozycji menu), wszystkie strony;
  // „Eksport zaznaczonych" = same ID. Plik: kolumny widoczne w tabeli, w jej kolejności,
  // z nagłówkami z układu.
  const listExport = useDocumentListExport<InternalDocRow>({
    target: 'internal-docs',
    filename: 'dokumenty-wewnetrzne',
    // Bez miesiąca kalendarza — to nawigacja widoku, nie filtr (plik = cały widok).
    buildFilterParams: () => buildListFilterParams({ includeCalendar: false }),
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extSlugSet.has(id) ? id : undefined),
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return columnsFor(getLabels, coreColumnsFor(getLabels), extColumnsFor(getLabels));
    },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.documents.internal.title'),
  });

  const listActions = useListActions({
    kartotekaKey: 'documents',
    documentClass: 'internal',
    entity: 'internal-docs',
    queryKey: [INTERNAL_DOCS_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    archivedView: showDeleted,
    handlers: { ...listExport.handlers },
    // „Pokaż zarchiwizowane" żyje w menu Akcje (nie jako osobny przycisk na pasku listy).
    archivedToggle: {
      active: showDeleted,
      onToggle: () => {
        setShowDeleted((prev) => !prev);
        controls.resetPage();
        setSelectedIds([]);
      },
    },
    testIdPrefix: 'internal-docs',
  });

  const docs = listQ.data?.internalDocs ?? [];

  return (
    <ModuleGuard moduleCode="crm_core">
      <main
        className="flex h-full min-h-0 flex-col bg-bg-secondary p-6"
        data-testid="internal-docs-page"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
            <div>
              <h1
                className="text-2xl font-bold text-text-primary"
                data-testid="internal-docs-title"
              >
                {t('tenant.documents.internal.title')}
              </h1>
              <p className="mt-1 text-sm text-text-tertiary">
                {t('tenant.documents.internal.subtitle')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="internal-docs" />
              <ListActionsMenu {...listActions.menuProps} />
              <ListBuilderMenu slug="internal" testIdPrefix="internal-docs" />
              <Button
                onClick={() => setShowTypePicker(true)}
                leftIcon={<Plus size={14} />}
                data-testid="internal-docs-add-btn"
              >
                {t('tenant.documents.internal.actions.add')}
              </Button>
            </div>
          </header>

          {narrowedTypeIds.length > 0 && employeeOptions.length > 0 ? (
            <div
              className="flex shrink-0 items-center gap-2"
              data-testid="internal-docs-employee-filter"
            >
              <span className="text-sm font-medium text-text-secondary">
                {t('tenant.documents.internal.filters.employeeLabel', {
                  defaultValue: 'Pracownik',
                })}
              </span>
              <MultiSelect
                value={employeeIds}
                onChange={(next) => {
                  setEmployeeIds(next);
                  controls.resetPage();
                }}
                options={employeeOptions}
                placeholder={t('tenant.documents.internal.filters.employeePlaceholder', {
                  defaultValue: 'Wszyscy pracownicy',
                })}
                className="w-72"
                data-testid="internal-docs-employee-filter-select"
              />
            </div>
          ) : null}

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {fieldsQ.isError ? (
              <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
            ) : (
              <ConfigurableTable<InternalDocRow>
                tableId={tableId}
                testIdPrefix="internal-docs"
                showOrdinal
                fillHeight
                {...viewModes.tableProps}
                /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): wyszukiwarka +
                   baton odśwież. „Pokaż zarchiwizowane" przeniesione do menu Akcje. */
                listToolbar={
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-[240px] flex-1">
                      <ListFilterBar
                        tableId={tableId}
                        columns={columns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.documents.internal.searchPlaceholder')}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={handleHeaderFiltersChange}
                        onRefresh={() =>
                          queryClient.invalidateQueries({ queryKey: [INTERNAL_DOCS_KEY] })
                        }
                        testIdPrefix="internal-docs"
                      />
                    </div>
                  </div>
                }
                columns={columns}
                data={docs}
                isLoading={listQ.isPending}
                getRowId={(row) => row.id}
                headerFilters={headerFilters}
                onHeaderFiltersChange={handleHeaderFiltersChange}
                filterable
                columnFilters={columnFilters}
                onColumnFiltersChange={(next) => {
                  setColumnFilters(next);
                  controls.resetPage();
                }}
                onVisibleColumnsChange={setVisibleColumnIds}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onRowContextMenu={listActions.onRowContextMenu}
                {...controls.tableProps}
                totalItems={listQ.data?.total ?? 0}
                isFetching={listQ.isFetching}
                loadError={listQ.error}
                onRetryLoad={() => void listQ.refetch()}
                onCalendarRangeChange={controls.setCalendarRange}
                onRowClick={(row) => navigate(withMenuItem(`/internal-docs/${row.id}`))}
                renderActions={(row) => (
                  <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {row.gcrecord !== null ? (
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmRestore(row)}
                        disabled={restoreMutation.isPending}
                        aria-label={t('tenant.documents.internal.actions.restore')}
                        data-testid={`internal-docs-row-${row.id}-restore`}
                      >
                        <RotateCcw aria-hidden="true" />
                      </IconButton>
                    ) : (
                      <IconButton
                        variant="danger"
                        size="sm"
                        onClick={() => setConfirmDelete(row)}
                        disabled={deleteMutation.isPending}
                        aria-label={t('common.delete')}
                        data-testid={`internal-docs-row-${row.id}-delete`}
                      >
                        <Trash2 aria-hidden="true" />
                      </IconButton>
                    )}
                  </div>
                )}
                actionsColumnLabel={t('tenant.documents.internal.columns.actions')}
                emptyIcon={Shield}
                emptyTitle={t('tenant.documents.internal.empty')}
              />
            )}
          </div>
        </div>

        <ConfirmDialog
          open={confirmDelete !== null}
          title={
            confirmDelete
              ? t('tenant.documents.internal.deleteConfirm', {
                  number: confirmDelete.systemNumber ?? confirmDelete.title ?? confirmDelete.id,
                })
              : ''
          }
          message={t('tenant.documents.internal.deleteConfirmBody')}
          confirmLabel={t('common.delete')}
          variant="danger"
          onConfirm={() => {
            if (confirmDelete) {
              deleteMutation.mutate(confirmDelete.id);
              setConfirmDelete(null);
            }
          }}
          onClose={() => setConfirmDelete(null)}
        />

        <ConfirmDialog
          open={confirmRestore !== null}
          title={
            confirmRestore
              ? t('tenant.documents.internal.restoreConfirm', {
                  number: confirmRestore.systemNumber ?? confirmRestore.title ?? confirmRestore.id,
                })
              : ''
          }
          message={t('tenant.documents.internal.restoreConfirmBody')}
          confirmLabel={t('tenant.documents.internal.actions.restore')}
          variant="primary"
          onConfirm={() => {
            if (confirmRestore) {
              restoreMutation.mutate(confirmRestore.id);
              setConfirmRestore(null);
            }
          }}
          onClose={() => setConfirmRestore(null)}
        />
      </main>
      <DocumentTypePickerModal
        open={showTypePicker}
        docClass="internal"
        allowedTypeIds={narrowedTypeIds}
        onSelect={(typeId, pickedCompanyId) => {
          setShowTypePicker(false);
          navigate(
            withMenuItem(
              `/internal-docs/new?documentTypeId=${typeId}&companyId=${pickedCompanyId}`,
            ),
          );
        }}
        onClose={() => setShowTypePicker(false)}
      />
      {listActions.overlays}
    </ModuleGuard>
  );
}
